import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import styled from '@mui/styles/styled';
import useTheme from '@mui/styles/useTheme';
import { config } from '../../config';
import { WarningAlert } from '../Alert/WarningAlert';
import { DateFormat } from '../../helpers/dateFormat';
import dayjs from 'dayjs';
import { Date } from '../Date/Date';
import { useLocalStorage } from '../../hooks/useLocalStorage';

interface IProps {
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  actions: React.ReactNode | React.ReactNode[];
  onSubmit?: React.FormEventHandler;
  open?: boolean;
  noBackdrop?: boolean;
  onClose?: () => void;
}

const Wrapper: FC<{ onSubmit?: React.FormEventHandler }> = ({ onSubmit, children }) => {
  if (onSubmit) return <form onSubmit={onSubmit}>{children}</form>;
  return <>{children}</>;
};

const Logo = styled('img')({
  width: '100%',
  maxWidth: '320px',
});

const ContentContainer = styled('div')(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '100%',
  background: theme.palette.background.default,
  padding: theme.spacing(6),
  marginTop: theme.spacing(6),
}));

const StyledDialogContent = styled(DialogContent)({
  overflow: 'hidden',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
});

const StyledWarning = styled(WarningAlert)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(-4),
}));

// 2026-08-17 06:00:00 CEST
const maintenance = dayjs(1786939200000);
const endOfMaintenance = maintenance.add(30, 'minutes');
const now = dayjs();

const guessedTimezone = dayjs.tz.guess();

export const AuthDialog: FC<IProps> = ({
  title,
  subtitle,
  children,
  actions,
  onSubmit,
  open = true,
  noBackdrop = true,
  onClose,
}) => {
  const theme = useTheme();

  const [maintenanceDismissed, setMaintenanceDismissed] = useLocalStorage('august2026maintenance', false);

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        hideBackdrop={noBackdrop}
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            overflow: 'hidden',
            maxWidth: 640,
          },
          elevation: 0,
        }}
        onClose={onClose}
      >
        <Wrapper onSubmit={onSubmit}>
          <StyledDialogContent>
            <Box textAlign="center" display="flex" flexDirection="column" alignItems="center">
              <Logo src={config.verticalLogo} />
              <Typography variant={'body2'}>Your virtual production solution</Typography>
            </Box>

            {!maintenanceDismissed && endOfMaintenance.isAfter(now) ? (
              <StyledWarning
                onClose={() => {
                  setMaintenanceDismissed(true);
                }}
              >
                <p>
                  A routine maintenance will be occurring on{' '}
                  <Date date={maintenance} format={DateFormat.MaintenanceTime} />
                  {guessedTimezone ? ` (${guessedTimezone})` : ''} and will take 30 minutes.
                </p>
                <p>Switching to offline mode is advised before if live broadcasts are expected at this time.</p>
              </StyledWarning>
            ) : null}

            <ContentContainer>
              {title ? (
                <Typography variant={'h4'} textAlign="center" mb={subtitle ? 0 : 4}>
                  {title}
                </Typography>
              ) : null}
              {subtitle ? (
                <Typography variant={'body1'} textAlign="center" color={theme.palette.text.disabled} mb={4}>
                  {subtitle}
                </Typography>
              ) : null}
              {children}
            </ContentContainer>
          </StyledDialogContent>

          <DialogActions style={{ gap: 24 }}>{actions}</DialogActions>
        </Wrapper>
      </Dialog>
    </>
  );
};
